import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { EVENT, KEY_COLLECTION, eventProgress, mytShortDate } from '../config/event'
import { isProfileComplete } from '../lib/profile-rules'
import { useNavigate } from 'react-router'
import { ArrowRight, Award, Building2, CalendarX2, CheckCircle2, ClipboardCheck, Clock3, ExternalLink, FolderOpen, Hotel, Images, Info, Lightbulb, MapPin, Megaphone, Mic, ScanQrCode, X } from 'lucide-react'
import type { AccommodationResponse, Announcement, ParticipantProfile, Slot } from '../lib/types'
import { cleanTitle, formatClock, relativeLabel, slotState, slotWindow } from '../lib/slot-time'
import { useAuthed } from '../lib/session'
import { useSlots, useTick } from '../features/useSlots'
import { api, ApiError, ID_EVENT } from '../lib/api'
import type { CertStatus } from '../lib/types'
import { SlotDetail } from '../features/SlotDetail'
import { Button } from '../ui/Button'
import { Notice, Skeleton, useToast } from '../ui/Feedback'
import { Sheet } from '../ui/Sheet'
import { ParticipantProfileForm } from './ParticipantProfileForm'

const NOOP = () => undefined

const ANN_DISMISS_KEY = 'cpd.announcements.dismissed.v1'

export function ScheduleRoute() {
  const session = useAuthed()
  const { slots, totalCheckin, loading, error, refresh } = useSlots()
  const now = useTick()
  const navigate = useNavigate()
  const toast = useToast()
  const [detail, setDetail] = useState<Slot | null>(null)
  const [hotelOpen, setHotelOpen] = useState(false)
  /*
   * How many ideas are on the board, for the tile. Only the public ones - a
   * delegate's own pending post rides along in the list reply and must not count.
   * Fetched once per visit; a stale figure here costs nothing.
   */
  const [ideaCount, setIdeaCount] = useState<number | null>(null)
  /*
   * Announcements: fetched on arrival and again whenever the phone wakes, the same
   * reason the slot store refetches - a room change posted while the screen was off
   * is the one that matters. Dismissals are per announcement, in localStorage, so a
   * notice read once does not nag; a new post is new and shows.
   */
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [dismissed, setDismissed] = useState<number[]>(() => {
    try { return JSON.parse(window.localStorage.getItem(ANN_DISMISS_KEY) ?? '[]') as number[] } catch { return [] }
  })
  useEffect(() => {
    let cancelled = false
    const load = () => {
      api.announcements(session.email, session.at)
        .then((res) => { if (!cancelled) setAnnouncements(res.announcements) })
        .catch(() => { /* Home works without them */ })
    }
    load()
    const onVisible = () => { if (document.visibilityState === 'visible') load() }
    document.addEventListener('visibilitychange', onVisible)
    return () => { cancelled = true; document.removeEventListener('visibilitychange', onVisible) }
  }, [session])
  const dismiss = (id: number) => {
    const next = [...dismissed, id].slice(-50)
    setDismissed(next)
    try { window.localStorage.setItem(ANN_DISMISS_KEY, JSON.stringify(next)) } catch { /* fine */ }
  }
  const showing = announcements.filter((a) => !dismissed.includes(a.id))
  useEffect(() => {
    let cancelled = false
    api.ideas(session.email, session.at)
      .then((res) => { if (!cancelled) setIdeaCount(res.ideas.filter((i) => i.status !== 'pending').length) })
      .catch(() => { /* no badge is fine */ })
    return () => { cancelled = true }
  }, [session])
  /*
   * The Certificate tile sits first once the certificate is ready and third before
   * that, so a fresh load had it painted third and hopping to first when the status
   * arrived. Remember the last answer per account and event: repeat visits start in
   * the right slot, and only a cold load waits (behind a placeholder, below).
   */
  const certCacheKey = `cpd.cert-status.v1:${ID_EVENT}:${session.email}`
  const [certStatus, setCertStatus] = useState<CertStatus | null>(() => {
    try {
      const raw = localStorage.getItem(certCacheKey)
      return raw ? (JSON.parse(raw) as CertStatus) : null
    } catch { return null }
  })
  const [certLoading, setCertLoading] = useState(true)
  const [accommodation, setAccommodation] = useState<AccommodationResponse | null>(null)
  /* Whether the organisers have set UiTM's attendance form. The address is fetched on tap. */
  const [attendanceLink, setAttendanceLink] = useState(false)
  useEffect(() => {
    let cancelled = false
    api.getSettings(session.email, session.at)
      .then((s) => { if (!cancelled) setAttendanceLink(s.attendance_link) })
      .catch(() => { /* no tile, same as no link */ })
    return () => { cancelled = true }
  }, [session.email, session.at])

  /*
   * Open a window first, then point it: a window.open after an await is a popup to
   * Safari and gets blocked. If the server says no (outside the event, not a
   * participant) the blank tab is closed and the reason shown instead.
   */
  async function openAttendanceForm() {
    /* No 'noopener' here: with it, window.open returns null even though the tab
       opened, so the fallback below fired too and this tab left the app as well.
       The opener is cut by hand once the tab exists. */
    const win = window.open('', '_blank')
    if (win) win.opener = null
    try {
      const { url } = await api.attendanceLink(session.at)
      if (win) win.location.href = url
      else window.location.assign(url)
    } catch (err) {
      win?.close()
      toast.show(err instanceof ApiError && err.body?.msg ? String(err.body.msg) : 'Could not open the attendance form', 'bad')
    }
  }
  const profileRequired = !isProfileComplete(session)
  const [profile, setProfile] = useState<ParticipantProfile | null>(null)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [profileAttempt, setProfileAttempt] = useState(0)

  const required = useMemo(() => slots.filter((s) => Boolean(s.qr_required)).length, [slots])
  const attendedPct = required ? Math.round((totalCheckin / required) * 100) : 0
  const rawName = session.name?.trim().replace(/\s+/g, ' ') || ''
  const shortName = rawName.split(/\s+/).slice(0, 2).join(' ')
  const greetingName = shortName && !/^(dr\.?|doctor|prof\.?|professor)$/i.test(shortName) ? shortName : null
  /* Recomputed as `now` ticks, so the running head turns over without a reload. */
  const progress = eventProgress(now)

  const live = useMemo(
    () => slots.find((s) => slotState(s, now) === 'live' && Boolean(s.qr_required))
      ?? slots.find((s) => slotState(s, now) === 'live'),
    [slots, now],
  )
  /*
   * Two, not three. Home answers "what now, what next" - a third row is the programme's
   * job, and the "View all" link beside the heading is one tap away.
   *
   * The slice must stay non-empty-preserving: `eventOver` below is derived from this
   * list, and `slotState` returns 'upcoming' for a slot whose time will not parse, so
   * shortening it cannot make a running event announce that it has finished.
   */
  const upcoming = useMemo(
    () => slots.filter((s) => slotState(s, now) === 'upcoming').slice(0, 2),
    [slots, now],
  )

  /*
   * Every session behind us. This is the one genuinely empty state the home screen has:
   * before the event and in the gaps between sessions there is always something
   * upcoming, so the screen has content. Afterwards it had a greeting, a ring and three
   * tiles above a screen of nothing.
   *
   * Safe by construction: slotState returns 'upcoming' for a slot whose time will not
   * parse, so a parsing failure keeps `upcoming` non-empty and can never make the app
   * announce that a running conference has finished.
   */
  const eventOver = !loading && slots.length > 0 && !live && upcoming.length === 0

  /*
   * `relativeLabel` has always returned "42 min left" for a running session; the home
   * card just never asked for it and printed the static time range instead. `now` comes
   * from useTick, so both of these move on their own.
   */
  /*
   * Whether the running session still wants anything from you.
   *
   * The live card is the loudest object on the screen, and once attendance is recorded
   * it is spending that volume on a session you are already finished with - while the
   * question you actually have at that moment is where to go next. So it stays a hero
   * only while it is actionable, and collapses to a line once it is not.
   *
   * A session with no QR requirement is settled from the start: there is nothing to do
   * about a coffee break.
   */
  const liveSettled = Boolean(live) && (!live?.qr_required || Boolean(live?.checkin_status))

  const liveRemaining = live ? relativeLabel(live, now) : ''
  /*
   * Defined once and placed in one of two positions.
   *
   * Normally it sits where it always did, below the attendance ring and the essentials
   * tiles. When the live session is settled the hero collapses, and this moves up into
   * the space that frees - because the only reason it was acceptable down there was
   * that something more urgent was above it.
   */
  const upcomingSection = upcoming.length > 0 ? (
    <section className="home__upcoming">
      <div className="home__section-head">
        <h2 className="display home__section-title">Upcoming</h2>
        <button className="home__viewall" onClick={() => navigate('/programme')}>
          View all <ArrowRight size={14} />
        </button>
      </div>
      <div className="home__upcoming-list">
        {upcoming.map((slot, index) => (
          <button key={slot.id} className="home__upcoming-row" style={{ '--i': index } as CSSProperties} onClick={() => setDetail(slot)}>
            {/* Rendered like everywhere else, 24-hour MYT - not the server's AM/PM label. */}
            <div className="home__upcoming-time tnum">
              {formatClock(slotWindow(slot).start)}–{formatClock(slotWindow(slot).end)}
            </div>
            <div className="home__upcoming-body">
              <strong>{slot.slot_name}</strong>
              <span><MapPin size={13} /> {slot.venue_name}</span>
              {slot.checkin_status ? <em>Checked in</em> : null}
            </div>
            <ArrowRight size={16} strokeWidth={1.5} />
          </button>
        ))}
      </div>
    </section>
  ) : null


  const liveElapsedPct = useMemo(() => {
    if (!live) return 0
    const { start, end } = slotWindow(live)
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0
    return Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100))
  }, [live, now])

  useEffect(() => {
    let cancelled = false
    api.certStatus(session.email).then((status) => {
      if (cancelled) return
      setCertStatus(status)
      try { localStorage.setItem(certCacheKey, JSON.stringify(status)) } catch { /* private mode */ }
    }).catch(() => {
      if (!cancelled) setCertStatus(null)
    }).finally(() => {
      if (!cancelled) setCertLoading(false)
    })
    return () => { cancelled = true }
  }, [session.email, certCacheKey])

  useEffect(() => {
    let cancelled = false
    api.getAccommodation(session.email, session.at).then((details) => {
      if (!cancelled) setAccommodation(details)
    }).catch(() => {
      if (!cancelled) setAccommodation(null)
    })
    return () => { cancelled = true }
  }, [session.at, session.email])

  useEffect(() => {
    if (!profileRequired) {
      setProfile(null)
      setProfileError(null)
      return undefined
    }
    let cancelled = false
    setProfile(null)
    setProfileError(null)
    api.getParticipantProfile(session.email, session.at)
      .then((result) => {
        if (!cancelled) setProfile(result)
      })
      .catch(() => {
        if (!cancelled) setProfileError('Could not load your event profile. Try again in a moment.')
      })
    return () => {
      cancelled = true
    }
  }, [profileAttempt, profileRequired, session.at, session.email])

  const certificateReady = Boolean(
    certStatus?.show_cert && certStatus.cert_eligable && certStatus.cert_url,
  )
  const certificateThreshold = EVENT.certificateAttendanceThreshold
  /*
   * The certificate tile's one line. The Certificate screen holds the full rule;
   * this is the state at a glance, and it replaces the "ready" card that used to
   * appear above the essentials only once the file existed.
   */
  const needed = Math.max(0, Math.ceil(required * certificateThreshold / 100) - totalCheckin)
  const certificateMeta = !certStatus ? 'Loading'
    : certificateReady ? 'Ready to open'
    : !certStatus.show_cert ? 'Released by the organisers after the event'
    : certStatus.attendance_exempt ? 'Committee, being prepared'
    : certStatus.cert_eligable ? 'Qualified, being prepared'
    : required > 0 ? `${needed} more session${needed === 1 ? '' : 's'} to qualify`
    : 'Attend sessions to qualify'

  /* The UiTM attendance button, placed directly after the Certificate tile wherever
     that tile sits: second while the certificate is ready, fourth otherwise. */
  const attendanceTile = attendanceLink ? (
          <button className="home__quick-tile" onClick={() => void openAttendanceForm()}>
            <span className="home__quick-icon home__quick-icon--attendance" aria-hidden="true"><ClipboardCheck size={20} strokeWidth={1.7} /></span>
            <span className="home__quick-copy">
              <span className="home__quick-label">Jam Latihan UiTM</span>
              <span className="home__quick-meta">Isi borang ePendaftaran</span>
            </span>
            <ExternalLink className="home__quick-arrow" size={17} strokeWidth={1.6} />
          </button>
  ) : null

  function startCheckIn(slot: Slot) {
    setDetail(null)
    void navigate(`/scan/${slot.id}`)
  }

  return (
    <main className="page page--home">
      {/*
        * A running head, not a masthead. Sign-in already prints the event's full name,
        * dates and venue at display size; repeating all three on every visit to the home
        * screen pushed the first session below the fold. A newspaper sets its masthead
        * once on the front page and gives inside pages a folio line — name, where you
        * are, date — closed by the same heavy-over-hair rule used elsewhere here.
        *
        * It is also the old sticky compact header. That was a second element that faded
        * in on scroll, which needed a scroll listener and a piece of state; a folio this
        * small can simply stay.
        */}
      <header className="home__folio">
        <strong>{EVENT.shortName}</strong>
        {progress.day ? (
          <span className="home__folio-day tnum">Day {progress.day} of {progress.total}</span>
        ) : null}
        {/* Which day it is matters while the event runs; before and after, only the range does. */}
        <span className="home__folio-when tnum">
          {progress.day ? mytShortDate(now) : EVENT.dateLabel}
        </span>
      </header>

      {/*
        * No track line here. It never changes, so it is the same kind of write-once
        * information the event name was, and it is already carried in the three places
        * that earn it: over the filtered list on Programme, as a labelled row on
        * Account, and in the sheet where it is actually confirmed. This screen answers
        * "what now", which the live card and attendance ring below do.
        */}
      {showing.length > 0 ? (
        <section className="home__announcements" aria-label="Announcements">
          {showing.map((a) => (
            <div key={a.id} className="home__announcement" data-level={a.level} role={a.level === 'urgent' ? 'alert' : 'status'}>
              <Megaphone size={17} strokeWidth={1.8} aria-hidden="true" />
              <p>{a.body}</p>
              <button type="button" onClick={() => dismiss(a.id)} aria-label="Dismiss"><X size={16} strokeWidth={2} /></button>
            </div>
          ))}
        </section>
      ) : null}

      <div className="home__greeting rise">
        <h1 className="display home__greeting-title">
          {greetingName ? <><em>Hello,</em>{greetingName}</> : 'Welcome back'}
        </h1>
      </div>

      {profileRequired ? (
        <Sheet open onClose={NOOP} title="Check your name and track" dismissible={false}>
          <section className="home__profile-prompt home__profile-prompt--modal" aria-labelledby="home-profile-title">
            <div className="home__profile-prompt-heading">
              <p className="kicker">Required before continuing</p>
              <h2 id="home-profile-title">Check your name and track</h2>
            </div>
            <p className="home__profile-prompt-copy">
              Confirm these details now. Your name is used on your certificate, and your track sets the programme and attendance requirement.
            </p>
            {profileError ? (
              <div className="home__profile-prompt-error">
                <p className="field__error" role="alert">{profileError}</p>
                <Button variant="quiet" size="sm" onClick={() => setProfileAttempt((attempt) => attempt + 1)}>
                  Try again
                </Button>
              </div>
            ) : null}
            {!profile && !profileError ? (
              <div className="profile-onboarding__loading" aria-busy="true">
                <Skeleton height="3.5rem" />
                <Skeleton height="3.5rem" />
              </div>
            ) : null}
            {profile ? (
              <ParticipantProfileForm profile={profile} submitLabel="Confirm and continue" />
            ) : null}
          </section>
        </Sheet>
      ) : null}

      {/*
        * The live session is the only thing on this screen that is true right now, so it
        * is the only thing that inverts. It used to be a white card with a faint tint,
        * indistinguishable at a glance from the attendance and certificate cards below
        * it. `data-surface="night"` is the app's existing dark-subtree mechanism - the
        * same one the scanner and venue display use - so every token inside flips and
        * the Button component comes along without a special case.
        *
        * No stock photograph, unlike the mockup this borrows from: there is no image per
        * session in the API, so one decorative crowd shot would repeat behind every
        * session all week and cost bytes on conference wifi.
        */}
      {live && liveSettled ? (
        <>
          {/*
            * The collapsed live card.
            *
            * Deliberately not the dark hero: the point of collapsing is to stop being
            * the loudest thing on the screen, and a smaller dark slab would still be
            * that. It keeps the two things that are still true and still changing - the
            * dot and the countdown - and the elapsed bar, which is what makes "38 min
            * left" spatial rather than a number to be read.
            */}
          <section className="home__section-card home__live-slim rise" style={{ '--i': 1 } as CSSProperties}>
            <span
              className="home__live-progress"
              style={{ '--elapsed': `${liveElapsedPct}%` } as CSSProperties}
              aria-hidden="true"
            />
            <span className="live-dot" aria-hidden="true" />
            <div className="home__live-slim-body">
              <p className="kicker home__live-slim-kicker">
                Live now
                {liveRemaining ? <span className="home__live-slim-left tnum"> &middot; {liveRemaining}</span> : null}
              </p>
              <strong>{cleanTitle(live.slot_name)}</strong>
              <span className="home__live-slim-meta">
                <MapPin size={13} /> {live.venue_name}
              </span>
              {/*
                * The same two states the hero had, at the size a settled fact deserves -
                * and stacked under the title rather than beside it.
                *
                * It used to be a filled, full-width capsule in the slot a primary button
                * occupies, so it read as something to tap and did nothing when tapped.
                * Putting it in its own column instead was the obvious next move and it
                * was also wrong: "Attendance recorded" is 188px of a 350px card, which
                * left the title 94px and wrapped it to six lines, so the collapsed card
                * came out 194px tall against the hero's 215. Barely a collapse at all.
                */}
              <span
                className={`home__settled${Boolean(live.qr_required) ? '' : ' home__settled--none'}`}
              >
                {Boolean(live.qr_required) ? <CheckCircle2 size={15} /> : <Info size={15} />}
                {Boolean(live.qr_required) ? 'Attendance recorded' : 'No check-in needed'}
              </span>
            </div>
          </section>
        </>
      ) : live ? (
        <section
          className="home__section-card home__live-card rise"
          data-surface="night"
          style={{ '--i': 1 } as CSSProperties}
        >
          {/*
            * Decoration, so it is a background rather than an <img>: nothing here is
            * content, it must never land in the accessibility tree, and a missing file
            * has to degrade to the gradient rather than to a broken-image icon.
            */}
          <span className="home__live-media" aria-hidden="true" />
          {/* Elapsed fraction of the session, which is what makes "42 min left" spatial. */}
          <span
            className="home__live-progress"
            style={{ '--elapsed': `${liveElapsedPct}%` } as CSSProperties}
            aria-hidden="true"
          />
          {/*
            * Label and countdown only. The time range moved down into the meta list -
            * three time-ish strings on one line crammed at 320px and broke "Live now"
            * across two lines. Down there it stacks, and the row that stays up here is
            * the one that changes.
            */}
          <div className="home__section-kicker">
            <span className="live-dot" />
            <span className="kicker home__live-label">Live now</span>
            {liveRemaining ? (
              <span className="home__live-remaining tnum">{liveRemaining}</span>
            ) : null}
          </div>
          <h2 className="home__card-title">{cleanTitle(live.slot_name)}</h2>
          <div className="home__meta-line">
            <span className="tnum"><Clock3 size={14} /> {formatClock(slotWindow(live).start)}–{formatClock(slotWindow(live).end)}</span>
            {/* Was a clock icon against the speaker's name, which said nothing true. */}
            {live.speaker ? <span><Mic size={14} /> {live.speaker}</span> : null}
            <span><MapPin size={14} /> {live.venue_name}</span>
          </div>
          {/*
            * The hero has exactly one job now, and it is a button.
            *
            * It used to carry three branches - check in, attendance recorded, no
            * check-in needed - but the last two are precisely the cases that now
            * collapse to the slim row above, so they were unreachable here. A settled
            * session never reaches this branch.
            */}
          <Button
            variant="primary"
            icon={<ScanQrCode size={16} strokeWidth={1.7} />}
            onClick={() => startCheckIn(live)}
          >
            Check in now
          </Button>
        </section>
      ) : eventOver ? (
        <section className="home__closing rise" style={{ '--i': 1 } as CSSProperties}>
          <p className="kicker home__closing-kicker">{EVENT.shortName} &middot; Ended</p>
          <h2 className="display home__closing-title">Thank you for joining us</h2>
          <p className="home__closing-body">
            {required === 0
              ? 'The programme has finished.'
              : totalCheckin >= required
                /* "12 of the 12" is how a counter talks, not a person. */
                ? `You attended all ${required} sessions that needed a check-in.`
                : `You attended ${totalCheckin} of the ${required} sessions that needed a check-in.`}
          </p>
          {/*
            * Certificates are only mentioned if the organisers have turned them on, and
            * the shortfall is stated plainly rather than left to be worked out from a
            * percentage ring. Saying nothing here would leave someone waiting for a
            * certificate that is not coming.
            */}
          {/*
            * Three outcomes, and until now only the middle one spoke.
            *
            * Eligible but no cert_url is the good ending and it was silent: the
            * certificate card below needs a URL to render, so someone who attended
            * everything saw a thank-you and nothing about the certificate they had
            * just earned. It is the common state right after an event, before the
            * organisers generate the files.
            *
            * The shortfall line needs `attendedPct < threshold` as well as
            * `!certificateReady`. The server decides eligibility from
            * countDistinct(id_slot) over the track's slots while this screen computes
            * a percentage over the qr_required ones; the denominators differ, so the
            * two can disagree, and without the guard this printed "A certificate needs
            * 70% attendance. You reached 100%." Where they disagree it says nothing and
            * the Certificate screen is the authority.
            */}
          {certStatus?.show_cert && required > 0 && !certificateReady ? (
            <>
              {certStatus.attendance_exempt ? (
                <p className="home__closing-good">
                  Committee members need no attendance record. Your certificate is being prepared.
                </p>
              ) : certStatus.cert_eligable ? (
                <p className="home__closing-good">
                  You met the attendance requirement. Your certificate is being prepared.
                </p>
              ) : attendedPct < certificateThreshold ? (
                <p className="home__closing-note">
                  A certificate needs {certificateThreshold}% attendance. You reached {attendedPct}%.
                </p>
              ) : null}
              <button
                type="button"
                className="home__closing-link"
                onClick={() => void navigate('/certificate')}
              >
                Certificate status
                <ArrowRight size={14} strokeWidth={1.8} />
              </button>
            </>
          ) : null}
          <span className="home__closing-mark" aria-hidden="true" />
        </section>
      ) : null}

      {/*
        * Directly under whatever describes now, in every state.
        *
        * It used to sit at the very bottom, beneath Event Essentials - so on the last
        * morning, with a session running, "where do I go after this" was below the fold
        * underneath a navigation menu. What is on now and what is on next are one
        * question asked twice; nothing belongs between them.
        */}
      {upcomingSection}

      <button className="home__attendance-card" onClick={() => navigate('/attendance')}>
        <div className="home__ring" style={{ '--pct': `${Math.min(100, attendedPct)}%` } as CSSProperties}>
          <span className="tnum">{attendedPct}%</span>
        </div>
        {/*
          * Two lines, not three. "Sessions completed" restated what the kicker above it
          * had already said and what the fraction beside it makes obvious - it was a
          * whole line of card height spent on nothing. The unit moves onto the figure,
          * where it does some work.
          */}
        <div className="home__attendance-copy">
          <span className="kicker">Your attendance</span>
          <strong>
            {totalCheckin} <span className="faint">/ {required || '—'}</span>
            <span className="home__attendance-unit"> sessions</span>
          </strong>
        </div>
        <ArrowRight size={18} strokeWidth={1.5} />
      </button>

      <section className="home__quick-section" aria-labelledby="home-essentials-title">
        <div className="home__quick-heading">
          <p id="home-essentials-title" className="kicker">Event essentials</p>
        </div>
        <div className="home__quick-links" aria-label="Event features">
        {certificateReady ? (
          /* Goes to the Certificate screen, not straight to the PDF: that screen shows
           * the name and count the certificate was issued on, then opens it. */
          <button className="home__quick-tile" onClick={() => navigate('/certificate')}>
            <span className="home__quick-icon home__quick-icon--cert-ready" aria-hidden="true"><Award size={20} strokeWidth={1.7} /></span>
            <span className="home__quick-copy">
              <span className="home__quick-label">Certificate</span>
              <span className="home__quick-meta">{certificateMeta}</span>
            </span>
            <ArrowRight className="home__quick-arrow" size={17} strokeWidth={1.6} />
          </button>
        ) : null}
        {certificateReady ? attendanceTile : null}
        <button className="home__quick-tile" onClick={() => setHotelOpen(true)}>
          <span className="home__quick-icon home__quick-icon--hotel" aria-hidden="true"><Building2 size={20} strokeWidth={1.7} /></span>
          <span className="home__quick-copy">
            <span className="home__quick-label">Hotel</span>
            <span className="home__quick-meta">View room details</span>
          </span>
          <ArrowRight className="home__quick-arrow" size={17} strokeWidth={1.6} />
        </button>
        <button className="home__quick-tile" onClick={() => navigate('/ideas')}>
          <span className="home__quick-icon home__quick-icon--ideas" aria-hidden="true"><Lightbulb size={20} strokeWidth={1.7} /></span>
          <span className="home__quick-copy">
            <span className="home__quick-label">
              CLiS - Bank Ideas
              {ideaCount ? <span className="home__quick-count tnum" aria-label={`${ideaCount} ideas on the board`}>{ideaCount}</span> : null}
            </span>
            <span className="home__quick-meta">Post and vote on ideas</span>
          </span>
          <ArrowRight className="home__quick-arrow" size={17} strokeWidth={1.6} />
        </button>
        {certLoading && !certStatus ? (
          <div className="home__quick-tile home__quick-tile--loading" aria-busy="true" aria-label="Loading certificate status">
            <span className="home__quick-icon skeleton" />
            <span className="home__quick-copy">
              <span className="skeleton home__quick-skel home__quick-skel--label" />
              <span className="skeleton home__quick-skel home__quick-skel--meta" />
            </span>
          </div>
        ) : certificateReady ? null : (
          <button className="home__quick-tile" onClick={() => navigate('/certificate')}>
            <span className="home__quick-icon home__quick-icon--cert" aria-hidden="true"><Award size={20} strokeWidth={1.7} /></span>
            <span className="home__quick-copy">
              <span className="home__quick-label">Certificate</span>
              <span className="home__quick-meta">{certificateMeta}</span>
            </span>
            <ArrowRight className="home__quick-arrow" size={17} strokeWidth={1.6} />
          </button>
        )}
        {certificateReady ? null : attendanceTile}
        <button className="home__quick-tile" onClick={() => navigate('/gallery')}>
          <span className="home__quick-icon home__quick-icon--gallery" aria-hidden="true"><Images size={20} strokeWidth={1.7} /></span>
          <span className="home__quick-copy">
            <span className="home__quick-label">Gallery</span>
            <span className="home__quick-meta">Event photos</span>
          </span>
          <ArrowRight className="home__quick-arrow" size={17} strokeWidth={1.6} />
        </button>
        <button className="home__quick-tile" onClick={() => navigate('/resources')}>
          <span className="home__quick-icon home__quick-icon--resources" aria-hidden="true"><FolderOpen size={20} strokeWidth={1.7} /></span>
          <span className="home__quick-copy">
            <span className="home__quick-label">Resources</span>
            <span className="home__quick-meta">Programme, slides & materials</span>
          </span>
          <ArrowRight className="home__quick-arrow" size={17} strokeWidth={1.6} />
        </button>
      </div>
      </section>

      {loading && slots.length === 0 ? <ScheduleSkeleton /> : null}

      {!loading && slots.length === 0 ? (
        <Notice
          icon={<CalendarX2 size={22} strokeWidth={1.3} />}
          title={error ? 'Could not load the programme' : 'Nothing scheduled yet'}
          body={error ? 'Check your connection and try again.' : 'Once sessions are assigned to your track they will appear here.'}
          action={{ label: 'Try again', onClick: refresh }}
        />
      ) : null}


      <SlotDetail slot={detail} now={now} onClose={() => setDetail(null)} onCheckIn={startCheckIn} />

      <Sheet open={hotelOpen} onClose={() => setHotelOpen(false)} title="Accommodation">
        <div className="hotel-sheet">
          <div className="hotel-sheet__icon" aria-hidden="true"><Hotel size={22} strokeWidth={1.5} /></div>
          <p className="kicker">Accommodation</p>
          <h2 className="display hotel-sheet__title">
            {accommodation?.status === 'own_arrangement' ? 'Your accommodation' : 'Your hotel room'}
          </h2>
          {/*
            * Three states, and only one of them is a room number. The plaque below sets
            * its text at 4.6rem because it exists to make "318" readable at arm's length
            * across a lobby; anything wordier in that slot renders as a headline, which
            * is what "Not needed" did. So the plaque appears only when there is a number
            * to put in it, and the other two states get a sentence at sentence size.
            */}
          {accommodation?.room_number ? (
            <>
              <div className="hotel-sheet__room">
                <span className="kicker">Room</span>
                <strong>{accommodation.room_number}</strong>
              </div>
              <div className="hotel-sheet__meta">
                <span><strong>Hotel:</strong> Event accommodation</span>
                <span><strong>Stay:</strong> {EVENT.dateLabel}</span>
                {KEY_COLLECTION ? <span>
                  <strong>Key:</strong>{' '}
                  {accommodation.key_collected_at
                    ? `collected ${new Intl.DateTimeFormat('en-MY', { timeZone: 'Asia/Kuala_Lumpur', weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(accommodation.key_collected_at))}`
                    : 'collect at the hotel desk'}
                </span> : null}
              </div>
              <p className="hotel-sheet__note">Room assignment supplied by the event organisers.</p>
            </>
          ) : accommodation?.status === 'own_arrangement' ? (
            <>
              <p className="hotel-sheet__state">You are arranging your own accommodation.</p>
              <p className="hotel-sheet__note">
                No room is being held for you. Speak to the organisers if that has changed.
              </p>
            </>
          ) : (
            <>
              <p className="hotel-sheet__state hotel-sheet__state--waiting">
                No room assigned yet.
              </p>
              <p className="hotel-sheet__note">
                Your room will appear here once the organisers have assigned it.
              </p>
            </>
          )}
        </div>
      </Sheet>
    </main>
  )
}

function ScheduleSkeleton() {
  return (
    <div style={{ paddingTop: 'var(--space-lg)' }} aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            display: 'grid',
            gridTemplateColumns: '3.25rem 0.75rem 1fr',
            gap: '0 var(--space-sm)',
            padding: 'var(--space-md) 0',
            borderBottom: '1px solid var(--rule)',
          }}
        >
          <Skeleton height="0.8rem" width="2.6rem" style={{ marginLeft: 'auto' }} />
          <Skeleton height="9px" width="9px" style={{ borderRadius: '50%', marginTop: 5 }} />
          <div style={{ display: 'grid', gap: 8 }}>
            <Skeleton height="1.1rem" width={`${88 - i * 9}%`} />
            <Skeleton height="0.75rem" width="55%" />
          </div>
        </div>
      ))}
    </div>
  )
}
